import type { ColDef, GetRowIdParams, GridOptions, ValueGetterParams } from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';

import { TableHeaderWithTooltipIcon, TextInputCell, TypeTextInputCellProps } from '~/components/common';
import {
  ReceiveRequestDetailDateCell,
  ReceiveRequestDetailInputCell,
  ReceiveRequestDetailSelectCell,
} from '~/components/receive';

import { formatNumber } from '~/utils/common';

import {
  LOT_NO_MAX_LENGTH,
  LOT_NO_REGEX,
  RECEIVE_MODIFICATION_TYPE,
  type TypeReceiveModificationType,
} from '~/constants';

import type { TypeReceiveDetailOrderItem } from '~/types/receive.types';

/**
 * [입고관리 > 입고요청관리] 입고요청상세 모달 > 입고 상품 테이블
 */
const getReceiveRegisterDetailModalColumnDefs = ({
  isReadOnly,
  hasPermissionNFA,
  tableData,
  setTableData,
}: {
  isReadOnly: boolean;
  hasPermissionNFA: boolean;
  tableData: TypeReceiveDetailOrderItem[];
  setTableData: (tableData: TypeReceiveDetailOrderItem[]) => void;
}) => {
  const gridOptions: GridOptions = {
    suppressScrollOnNewData: true,
    suppressRowVirtualisation: false,
    getRowId: (params: GetRowIdParams<TypeReceiveDetailOrderItem>) => String(params.data.index),
  };

  const columnDefs: ColDef<TypeReceiveDetailOrderItem>[] = [
    {
      headerName: '',
      valueFormatter: () => '',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 44,
    },
    {
      field: 'goodsCode',
      headerName: '상품코드',
      width: 140,
    },
    {
      field: 'nsid',
      headerName: 'NSID',
      width: 180,
      hide: !hasPermissionNFA,
    },
    {
      field: 'alternativeCode',
      headerName: '상품바코드',
      width: 150,
    },
    {
      field: 'goodsName',
      headerName: '상품명',
      minWidth: 240,
      flex: 1,
    },
    {
      field: 'kurlyKeepTypeDesc',
      headerName: '보관유형',
      width: 100,
    },
    {
      headerName: '기한/일자 사용여부',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '유통/소비기한 또는 제조일자가 없다면\n[사용] 체크박스를 해제해 주세요.',
        tooltipMaxWidth: '203px',
      },
      cellRenderer: ReceiveRequestDetailDateCell,
      cellRendererParams: { isReadOnly: isReadOnly },
      cellClass: 'overflow-visible',
      width: 309,
      minWidth: 309,
    },
    {
      field: 'usePallet',
      headerName: '파렛트 사용여부',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '상품을 파렛트에 적재 후 입고하면\n[사용함]으로 선택해 주세요.',
        tooltipMaxWidth: '182px',
      },
      cellRenderer: (row: CustomCellRendererProps) => ReceiveRequestDetailSelectCell(row, isReadOnly),
      cellRendererParams: { disabled: isReadOnly },
      cellClass: 'overflow-visible',
      width: 136,
      minWidth: 136,
    },
    {
      field: 'lotNo',
      headerName: 'LOT 번호',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription:
          '상품의 제조 또는 생산 단위를 구분하기 위한 고유 식별 번호입니다.\n입력된 LOT 번호는 재고 추적 및 이력 관리에 활용됩니다.',
        tooltipMaxWidth: '335px',
      },
      width: 200,
      minWidth: 200,
      cellClass: 'overflow-visible',
      cellRenderer: TextInputCell,
      cellRendererParams: ({
        node: { rowIndex },
        data,
      }: TypeTextInputCellProps<TypeReceiveDetailOrderItem, TypeReceiveDetailOrderItem['lotNo']>) => {
        const isModified = data?.modifiedFields?.includes('lotNo') ?? false;

        return {
          placeholder: `${LOT_NO_MAX_LENGTH}자 이하 입력`,
          maxLength: LOT_NO_MAX_LENGTH,
          isModified,
          disabled: isReadOnly,
          onChange: (value: string) => {
            if (!LOT_NO_REGEX.test(value)) return;

            if (!data || rowIndex === null) return;

            setTableData([
              ...tableData.slice(0, rowIndex),
              {
                ...data,
                lotNo: value,
                modificationType: RECEIVE_MODIFICATION_TYPE.MODIFY as TypeReceiveModificationType,
                modifiedFields: isModified ? data.modifiedFields : [...(data.modifiedFields ?? []), 'lotNo'],
              },
              ...tableData.slice(rowIndex + 1),
            ]);
          },
        };
      },
    },
    {
      field: 'boxQty',
      headerName: '수량(BOX)',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '수량(BOX) 또는 수량(EA)중\n하나는 필수로 입력해야 합니다.',
        tooltipMaxWidth: '171px',
      },
      cellRenderer: ReceiveRequestDetailInputCell,
      cellRendererParams: { disabled: isReadOnly },
      cellClass: 'overflow-visible',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'boxPerQty',
      headerName: '입수(BOX당)',
      cellRenderer: ReceiveRequestDetailInputCell,
      cellRendererParams: { disabled: isReadOnly },
      cellClass: 'overflow-visible',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'eachQty',
      headerName: '수량(EA)',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '수량(BOX) 또는 수량(EA)중\n하나는 필수로 입력해야 합니다.',
        tooltipMaxWidth: '171px',
      },
      cellRenderer: ReceiveRequestDetailInputCell,
      cellRendererParams: { disabled: isReadOnly },
      cellClass: 'overflow-visible',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'totalQty',
      headerName: '총 수량',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '수량(BOX)*입수(BOX당)+수량(EA)로\n자동 계산됩니다.',
        tooltipMaxWidth: '210px',
      },
      cellStyle: { textAlign: 'right' },
      valueGetter: ({ data }: ValueGetterParams<TypeReceiveDetailOrderItem>) => {
        if (!data) return '-';

        const { boxQty, boxPerQty, eachQty } = data;
        const totalQty = boxQty * boxPerQty + eachQty;
        return totalQty ? formatNumber(totalQty) : '-';
      },
      width: 112,
    },
  ];

  return { gridOptions, columnDefs };
};

export default getReceiveRegisterDetailModalColumnDefs;
